/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useState } from 'react';
import { type ContentStackButton } from '../../interface';
import { Button, View, Text, type ButtonProps, type ViewProps } from '@az/starc-ui';
import { CMSLink } from '../CMSLink/CMSLink';
import styles from './CMSButton.module.scss';
import { defaultStoreUS } from '@/constants/locale';
import { useSignInFlowType } from '@/components/SignInFlowTypeContext';
import { isModalLink } from '../../utils/isModalLink';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';
import { useLoginConfirmation } from '@/features/account/hooks/useLoginConfirmation';
import type { StandaloneOnlineMergeModalsType } from '@/features/mergeRewards';
import { useAppData } from '@/hooks/useAppData';
import React from 'react';

const LazySignInModalV2 = dynamic(() => import('@/components/SignInModalV2'), {
  loading: () => <ProgressModal noScroll />,
});

const LazySearchStoreModal = dynamic(
  () =>
    import('@/components/Store/StoreModal/SearchStoreModal').then((mod) => mod.SearchStoreModal),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

const LazyMyStoreModal = dynamic(
  () => import('@/components/Store/StoreModal/MyStoreModal').then((mod) => mod.MyStoreModal),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

type ButtonVariant = Exclude<
  Lowercase<NonNullable<ContentStackButton['theme']>>,
  'chevron' | 'pill' // TODO: remove `Exclude` after updating STARC version
>;

type Props = {
  options: ContentStackButton;
  size?: ButtonProps['size'];
  justify?: ViewProps<'div'>['justify'];
  ariaLabel?: string;
  textClassName?: string;
  onAnalyticsClick?: () => void;
};

export function CMSButton({
  justify = 'center',
  options,
  size,
  ariaLabel,
  textClassName,
  onAnalyticsClick,
}: Props) {
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const toggleStoreModal = () => {
    setStoreModalOpen((s) => !s);
  };
  const { data: storeData } = useStoreDetailsData();
  const storeNumber = storeData?.storeNumber ?? defaultStoreUS;

  const [open, setOpen] = useState(false);

  const { setFlowTypeFn } = useSignInFlowType();
  const { appData } = useAppData();
  const isLoggedIn = appData.userAuthenticated;

  const { showRewardSigninPopup } = useLoginConfirmation();

  const { data: userData } = useMyProfile();

  const handleStandaloneRewards = (id: string) => {
    const normalized = id.toLowerCase();
    const isJoinFlow = normalized.includes('/create') || normalized.includes('join');

    const view: StandaloneOnlineMergeModalsType = isJoinFlow
      ? 'SIGN_ME_UP_FORM'
      : 'LINK_REWARDS_ACC';

    showRewardSigninPopup(view);
  };

  const handleModalOpen = (id: string) => {
    const isStandaloneRewards =
      (id.includes('/create') || id.includes('/signin')) && userData?.userType === 4;
    if (isStandaloneRewards) {
      handleStandaloneRewards(id);
      return;
    }

    switch (id) {
      case '[search_store_modal]':
        toggleStoreModal();
        break;
      case '[sign_in_modal]':
      case '[create_account_modal]':
        if (isLoggedIn) {
          break;
        }

        setFlowTypeFn('login');
        setOpen(true);
        break;
      case '[cookie_preference_center_modal]':
        window.OneTrust?.ToggleInfoDisplay();
        break;
    }
  };

  const isModalButton = isModalLink(options.link?.href ?? '');
  const isStandaloneSignInModal =
    (options.link?.href.includes('/create') || options.link?.href.includes('/signin')) &&
    userData?.userType === 4;
  const isSignInModal =
    options.link?.href === '[sign_in_modal]' || options.link?.href === '[create_account_modal]';
  const isModalButtonDisabled = isSignInModal && isLoggedIn;
  const StoreModal = storeNumber !== defaultStoreUS ? LazyMyStoreModal : LazySearchStoreModal;

  return (
    <>
      <View justify={justify} direction="row">
        {isModalButton || isStandaloneSignInModal ? (
          <Button
            /* https://github.com/microsoft/TypeScript/issues/44268 */
            variant={(options.theme?.toLowerCase() as ButtonVariant | undefined) ?? 'primary'}
            className={styles.button}
            size={size}
            onClick={() => {
              handleModalOpen(options.link?.href ?? '');
            }}
            disabled={isModalButtonDisabled}
            aria-haspopup="dialog"
            aria-label={ariaLabel ?? options.link?.title}
          >
            <Text size="087">{options.link?.title}</Text>
          </Button>
        ) : (
          <CMSLink to={options.link?.href ?? ''} title={ariaLabel ?? options.link?.title}>
            <Button
              /* https://github.com/microsoft/TypeScript/issues/44268 */
              variant={(options.theme?.toLowerCase() as ButtonVariant | undefined) ?? 'primary'}
              as="div"
              className={styles.button}
              size={size}
              tabIndex={-1}
              onClick={onAnalyticsClick}
              attributes={{
                'data-testid': 'cms-internal-button',
                'aria-hidden': true,
              }}
            >
              <Text size="087" className={textClassName}>
                {options.link?.title}
              </Text>
            </Button>
          </CMSLink>
        )}
      </View>
      {isSignInModal && <LazySignInModalV2 isOpen={open} onClose={() => setOpen(false)} />}
      {storeModalOpen && <StoreModal closeStoreModal={toggleStoreModal} />}
    </>
  );
}
